import { CACHE_TAG_REVALIDATE } from "@/app/_api/cacheTagRegistry";
import { fetchWithCache } from "@/app/_api/fetchWithCache";
import { logger } from "@utilities/logger";

// ElevenLabs iOS app — see Languages CMS docs (`iOSAppId`).
const APPLE_APP_ID = "6743162587";
const ITUNES_LOOKUP_URL = `https://itunes.apple.com/lookup?id=${APPLE_APP_ID}`;

export interface ProductRating {
  ratingValue: string;
  ratingCount: number;
  bestRating: string;
  worstRating: string;
}

// Last-known-good values: keep the schema valid even if iTunes is unreachable
// or returns a malformed payload. Without a fallback, a transient outage would
// reintroduce the "missing aggregateRating" rich-result error.
const FALLBACK: ProductRating = {
  ratingValue: "4.8",
  ratingCount: 7415,
  bestRating: "5",
  worstRating: "1",
};

interface ItunesLookupResponse {
  results?: Array<{
    averageUserRating?: number;
    userRatingCount?: number;
  }>;
}

export async function getAppleAppRating(): Promise<ProductRating> {
  try {
    const data = await fetchWithCache<ItunesLookupResponse>(
      ITUNES_LOOKUP_URL,
      { method: "GET" },
      {
        keys: ["product-ratings", APPLE_APP_ID],
        tags: ["product-ratings"],
        revalidate: CACHE_TAG_REVALIDATE["product-ratings"],
      },
    );
    const result = data.results?.[0];
    if (!result?.averageUserRating || !result?.userRatingCount) {
      logger.warn("[ProductRatings] iTunes Search API returned no rating fields; using fallback");
      return FALLBACK;
    }
    return {
      ratingValue: result.averageUserRating.toFixed(1),
      ratingCount: result.userRatingCount,
      bestRating: "5",
      worstRating: "1",
    };
  } catch (err) {
    logger.error(err instanceof Error ? err : null, "[ProductRatings] Failed to fetch iTunes app rating");
    return FALLBACK;
  }
}
